import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Sun, Loader2, Plus, Save, Trash2, RefreshCw, X, Pencil, Package, Battery, Zap, Plug, Wrench, Upload, FileText, CheckCircle2, AlertTriangle, Filter, Copy } from "lucide-react";
import api from "@/lib/api";
import { useReloadBus } from "@/lib/reloadBus";
import { AppHeader } from "@/components/AppHeader";
import { EuroInput } from "@/components/EuroInput";
import { PvDatasheetField } from "@/components/datasheets/PvDatasheetField";

const inputCls = "h-10 w-full bg-[#09090B] border border-zinc-700 rounded-lg px-3 text-zinc-50 focus:border-[#00E575] outline-none text-[13px] font-medium";
const labelCls = "block text-[12px] uppercase tracking-widest text-zinc-200 mb-1 font-bold";

const eur = (v) => {
  try { return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(Number(v) || 0); }
  catch { return `${v} €`; }
};

const TYPES = [
  { key: "module",   label: "Module",          icon: Sun,     unitLabel: "kWp" },
  { key: "inverter", label: "Wechselrichter",  icon: Zap,     unitLabel: "kW" },
  { key: "battery",  label: "Speicher",        icon: Battery, unitLabel: "kWh" },
  { key: "wallbox",  label: "Wallbox",         icon: Plug,    unitLabel: "kW" },
  { key: "addon",    label: "Zusatzleistungen", icon: Wrench,  unitLabel: "—" },
];

const emptyDraft = (type = "module") => ({
  type,
  brand: "",
  model: "",
  kwp_or_kw: 0,
  price_eur: 0,
  unit: type === "addon" ? "pauschal" : "Stk",
  specs: {},
  active: true,
});

// Sub-Komponente für eine Zeile in der Import-Preview: eigener State für den
// Preis-String (damit deutsche Formatierung während Eingabe erhalten bleibt) und
// aufklappbare Detail-Zeile mit Beschreibung/Eigenschaften/Vorteile aus specs.
function ImportRowFragment({ idx, row, onUpdate, onRemove, types }) {
  const [expanded, setExpanded] = useState(false);
  const [priceStr, setPriceStr] = useState(
    () => (Number(row.price_eur) || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  );
  const specs = row.specs || {};
  const hasSpecs = !!(specs.description || specs.properties || specs.advantages || specs.ek_price_eur);

  const commitPrice = (raw) => {
    // "1.868,06" → 1868.06 · "1868" → 1868 · "1868.06" → 1868.06
    const norm = String(raw).replace(/\./g, "").replace(",", ".");
    const num = Number(norm);
    if (!isNaN(num)) {
      onUpdate(idx, { price_eur: num });
      setPriceStr(num.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
    } else {
      // reset zu letztem gültigen Wert
      setPriceStr((Number(row.price_eur) || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
    }
  };

  return (
    <>
      <tr data-testid={`pv-import-row-${idx}`} className="border-b border-zinc-900 hover:bg-[#0F0F11]">
        <td className="py-1.5 pr-2">
          <select
            value={row.type}
            onChange={(e) => onUpdate(idx, { type: e.target.value, unit: e.target.value === "addon" && row.unit === "Stk" ? "pauschal" : row.unit })}
            className="h-8 bg-[#09090B] border border-zinc-700 rounded px-2 text-[11.5px] font-medium"
            data-testid={`pv-import-type-${idx}`}
          >
            {types.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
        </td>
        <td className="py-1.5 pr-2">
          <input
            type="text" value={row.brand}
            onChange={(e) => onUpdate(idx, { brand: e.target.value })}
            className="h-8 w-32 bg-[#09090B] border border-zinc-700 rounded px-2 text-[11.5px]"
            data-testid={`pv-import-brand-${idx}`}
          />
        </td>
        <td className="py-1.5 pr-2">
          <input
            type="text" value={row.model}
            onChange={(e) => onUpdate(idx, { model: e.target.value })}
            className="h-8 w-full min-w-[220px] bg-[#09090B] border border-zinc-700 rounded px-2 text-[11.5px]"
            data-testid={`pv-import-model-${idx}`}
          />
        </td>
        <td className="py-1.5 pr-2">
          <input
            type="number" step="0.001" value={row.kwp_or_kw}
            onChange={(e) => onUpdate(idx, { kwp_or_kw: e.target.value })}
            className="h-8 w-20 bg-[#09090B] border border-zinc-700 rounded px-2 text-right text-[11.5px]"
            data-testid={`pv-import-kw-${idx}`}
          />
        </td>
        <td className="py-1.5 pr-2">
          <div className="relative">
            <input
              type="text" inputMode="decimal" value={priceStr}
              onChange={(e) => setPriceStr(e.target.value)}
              onBlur={(e) => commitPrice(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.currentTarget.blur(); } }}
              className="h-8 w-28 bg-[#09090B] border border-zinc-700 rounded pl-2 pr-6 text-right text-[11.5px] font-bold text-[#00E575] focus:border-[#00E575] outline-none"
              data-testid={`pv-import-price-${idx}`}
            />
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[12px] text-zinc-300 pointer-events-none">€</span>
          </div>
        </td>
        <td className="py-1.5 pr-2">
          <select
            value={row.unit || "Stk"}
            onChange={(e) => onUpdate(idx, { unit: e.target.value })}
            className="h-8 bg-[#09090B] border border-zinc-700 rounded px-2 text-[11.5px]"
            data-testid={`pv-import-unit-${idx}`}
          >
            <option value="Stk">Stk</option>
            <option value="pauschal">pauschal</option>
            <option value="m">m</option>
            <option value="m²">m²</option>
            <option value="h">h</option>
          </select>
        </td>
        <td className="py-1.5 pr-2 text-center">
          {hasSpecs ? (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              data-testid={`pv-import-info-${idx}`}
              className={`h-7 px-2 rounded text-[10.5px] font-bold inline-flex items-center gap-1 border ${expanded ? "bg-[#00E575]/15 border-[#00E575]/50 text-[#00E575]" : "bg-zinc-800/50 border-zinc-700 text-zinc-300 hover:text-zinc-100"}`}
              title="Beschreibung, Eigenschaften und Vorteile anzeigen/verbergen"
            >
              {expanded ? "▼ Info" : "▶ Info"}
            </button>
          ) : (
            <span className="text-zinc-400 text-[12px]">—</span>
          )}
        </td>
        <td className="py-1.5 text-right">
          <button
            onClick={() => onRemove(idx)}
            data-testid={`pv-import-remove-${idx}`}
            className="h-7 w-7 rounded bg-red-500/10 border border-red-500/40 text-red-400 hover:bg-red-500/20 inline-flex items-center justify-center"
            title="Zeile entfernen"
          >
            <X size={12} />
          </button>
        </td>
      </tr>
      {expanded && hasSpecs && (
        <tr className="bg-[#0F0F11] border-b border-zinc-900" data-testid={`pv-import-details-${idx}`}>
          <td colSpan={8} className="py-3 px-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-[11.5px]">
              {specs.description && (
                <div>
                  <div className="text-[9.5px] uppercase tracking-widest text-emerald-300 font-bold mb-1">Beschreibung</div>
                  <p className="text-zinc-200 whitespace-pre-wrap leading-relaxed">{specs.description}</p>
                </div>
              )}
              {specs.properties && (
                <div>
                  <div className="text-[9.5px] uppercase tracking-widest text-amber-300 font-bold mb-1">Eigenschaften</div>
                  <p className="text-zinc-200 whitespace-pre-wrap leading-relaxed">{specs.properties}</p>
                </div>
              )}
              {specs.advantages && (
                <div>
                  <div className="text-[9.5px] uppercase tracking-widest text-sky-300 font-bold mb-1">Vorteile</div>
                  <p className="text-zinc-200 whitespace-pre-wrap leading-relaxed">{specs.advantages}</p>
                </div>
              )}
            </div>
            {specs.ek_price_eur > 0 && (
              <div className="mt-2 pt-2 border-t border-zinc-800 text-[10.5px] text-zinc-400 flex items-center gap-3">
                <span>EK-Preis (Einkauf): <b className="text-zinc-200 font-mono">{Number(specs.ek_price_eur).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</b></span>
                {specs.import_section && <span>· Herkunft: <b className="text-zinc-200">{specs.import_section}</b></span>}
                {specs.import_brand_group && <span>· Marken-Gruppe: <b className="text-zinc-200">{specs.import_brand_group}</b></span>}
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

export default function AdminPvCatalog({ embedded = false }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [editor, setEditor] = useState(null);
  const [activeTab, setActiveTab] = useState("module");
  const [showInactive, setShowInactive] = useState(false);

  // PDF/Bild-Import (LLM-gestützt) — Zwei-Schritt-Verfahren:
  // 1) File hochladen → Preview-Zeilen (nicht in DB), User editiert Preise/Namen
  // 2) Commit → alle bestätigten Zeilen ins pv_components collection
  const importFileRef = useRef(null);
  const [importState, setImportState] = useState(null);
  //   null                                        → kein Import aktiv
  //   { phase:"extract", filename }               → LLM läuft
  //   { phase:"preview", rows, source_tag, ... }  → editierbare Preview
  //   { phase:"commit" }                          → wird eingespielt

  // Iter88zz9y — Foto-Upload für PV-Komponente (analog WP)
  const [uploadingImg, setUploadingImg] = useState(false);
  const uploadComponentImage = async (file) => {
    if (!editor || editor.isNew) {
      toast.error("Bitte zuerst die Komponente speichern, dann kannst du ein Bild hochladen.");
      return;
    }
    if (!file) return;
    if (!file.type?.startsWith("image/")) {
      toast.error("Nur Bilddateien erlaubt");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Datei zu groß — max. 10 MB");
      return;
    }
    setUploadingImg(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/pv/catalog/${editor.id}/image`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setEditor({ ...editor, image_url: data.image_url });
      toast.success("Bild hochgeladen · automatisch auf 800×800 zugeschnitten");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload fehlgeschlagen");
    } finally {
      setUploadingImg(false);
    }
  };

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/pv/catalog");
      setRows(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Komponenten konnten nicht geladen werden");
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);
  // Iter88zbu — Silent-Reload nach jeder Mutation.
  useReloadBus("*", () => load(true));

  const seedDemo = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post("/pv/catalog/seed-demo");
      if (data.skipped) toast.info(`Bereits ${data.existing} Komponenten vorhanden, kein Seed nötig`);
      else toast.success(`${data.inserted} Beispiel-Komponenten hinzugefügt`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Seed fehlgeschlagen");
    } finally { setSeeding(false); }
  };

  const [sortKey, setSortKey] = useState("brand");
  const [sortDir, setSortDir] = useState("asc"); // "asc" | "desc"

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };

  const tabRows = useMemo(() => {
    const filtered = rows.filter((r) => r.type === activeTab && (showInactive || r.active !== false));
    // Iter88n (2026-02-19): Sortierbare Spalten Marke/Modell/Preis für
    // schnelleres Duplikat-Erkennen. Klick auf Header togglet asc/desc.
    const dir = sortDir === "desc" ? -1 : 1;
    const cmp = (a, b) => {
      const av = a?.[sortKey];
      const bv = b?.[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
      return String(av).localeCompare(String(bv), "de", { sensitivity: "base", numeric: true }) * dir;
    };
    return [...filtered].sort(cmp);
  }, [rows, activeTab, showInactive, sortKey, sortDir]);

  const counts = useMemo(() => {
    const out = {};
    for (const t of TYPES) {
      out[t.key] = rows.filter((r) => r.type === t.key && r.active !== false).length;
    }
    return out;
  }, [rows]);

  const openNew = () => setEditor({ isNew: true, ...emptyDraft(activeTab) });
  const openEdit = (row) => setEditor({
    isNew: false, id: row.id,
    type: row.type, brand: row.brand || "", model: row.model || "",
    kwp_or_kw: row.kwp_or_kw ?? 0, price_eur: row.price_eur ?? 0,
    unit: row.unit || (row.type === "addon" ? "pauschal" : "Stk"),
    specs: row.specs || {}, active: row.active !== false,
    datasheet_storage_path: row.datasheet_storage_path || null,
    datasheet_filename: row.datasheet_filename || null,
    datasheet_size_bytes: row.datasheet_size_bytes || null,
    datasheet_uploaded_at: row.datasheet_uploaded_at || null,
  });

  const save = async () => {
    if (!editor) return;
    if (!editor.brand || !editor.model) {
      toast.error("Marke und Modell/Bezeichnung sind Pflicht");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        type: editor.type, brand: editor.brand, model: editor.model,
        kwp_or_kw: Number(editor.kwp_or_kw) || 0,
        price_eur: Number(editor.price_eur) || 0,
        unit: editor.unit || "Stk",
        specs: editor.specs || {}, active: editor.active,
        image_url: (editor.image_url || "").trim() || null,
      };
      if (editor.isNew) {
        await api.post("/pv/catalog", payload);
        toast.success("Komponente angelegt");
      } else {
        await api.patch(`/pv/catalog/${editor.id}`, payload);
        toast.success("Änderungen gespeichert");
      }
      setEditor(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setSaving(false); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Komponente "${row.brand} ${row.model}" deaktivieren?\nBestehende Konfigurationen bleiben unverändert.`)) return;
    try {
      await api.delete(`/pv/catalog/${row.id}`);
      toast.success("Deaktiviert");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehlgeschlagen");
    }
  };

  const reactivate = async (row) => {
    try {
      await api.post(`/pv/catalog/${row.id}/reactivate`);
      toast.success("Reaktiviert");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehlgeschlagen");
    }
  };

  // Iter88zb — Duplizieren einer PV-Komponente. Analog zu WP-Komponenten:
  // Prompt für den neuen Modellnamen (Default „ (Kopie)"), POST auf Duplicate-
  // Endpoint, danach optional PATCH um den Namen zu individualisieren.
  // Danach Editor direkt öffnen, damit Preis/Specs sofort angepasst werden können.
  const duplicateRow = async (row) => {
    const suggested = `${row.model || "Modell"} (Kopie)`;
    const chosen = window.prompt("Modellname der Kopie:", suggested);
    if (chosen === null) return;
    const finalName = chosen.trim() || suggested;
    try {
      const { data: copy } = await api.post(`/pv/catalog/${row.id}/duplicate`);
      if (finalName !== copy.model) {
        try {
          await api.patch(`/pv/catalog/${copy.id}`, {
            type: copy.type,
            brand: copy.brand,
            model: finalName,
            kwp_or_kw: copy.kwp_or_kw ?? 0,
            price_eur: copy.price_eur || 0,
            specs: copy.specs || {},
            unit: copy.unit || "Stk",
            active: true,
          });
          copy.model = finalName;
        } catch { /* Kopie bleibt mit Default-Namen bestehen */ }
      }
      toast.success(`Kopiert: „${copy.brand} ${finalName}"`);
      await load();
      openEdit({ ...copy, model: finalName });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kopieren fehlgeschlagen");
    }
  };

  // Inline-Preis-Edit (analog WP-Komponenten): Klick auf die Preis-Zelle
  // öffnet ein Input direkt in der Tabelle. Speichern per Enter/Save,
  // Abbrechen per Esc. PATCH schickt volle Struktur; PV-Pakete rechnen
  // beim nächsten Load automatisch mit dem neuen Preis.
  const [inlineEdit, setInlineEdit] = useState(null); // { id, value, saving }
  const startInlineEdit = (r) => setInlineEdit({ id: r.id, value: String(r.price_eur ?? 0), saving: false });
  const cancelInlineEdit = () => setInlineEdit(null);
  const commitInlineEdit = async () => {
    if (!inlineEdit) return;
    const num = Number(String(inlineEdit.value).replace(",", ".").trim());
    if (!Number.isFinite(num) || num < 0) {
      toast.error("Bitte einen gültigen Preis eingeben (z. B. 249,90)");
      return;
    }
    const row = rows.find(x => x.id === inlineEdit.id);
    if (!row) { setInlineEdit(null); return; }
    setInlineEdit({ ...inlineEdit, saving: true });
    try {
      await api.patch(`/pv/catalog/${inlineEdit.id}`, {
        type: row.type, brand: row.brand, model: row.model,
        kwp_or_kw: row.kwp_or_kw ?? 0, price_eur: num,
        unit: row.unit || "Stk", specs: row.specs || {},
        active: row.active !== false,
      });
      setRows(prev => prev.map(x => x.id === inlineEdit.id ? { ...x, price_eur: num } : x));
      toast.success(`Preis aktualisiert: ${eur(num)}`);
      setInlineEdit(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
      setInlineEdit({ ...inlineEdit, saving: false });
    }
  };

  // --- PDF/Bild-Import via LLM ---------------------------------------------
  const startImport = () => importFileRef.current?.click();

  // --- Duplikate bereinigen ------------------------------------------------
  const [dedupeState, setDedupeState] = useState(null); // null | {phase:'preview'|'running'|'done', ...}

  const openDedupePreview = async () => {
    setDedupeState({ phase: "loading" });
    try {
      const { data } = await api.post("/admin/pv-catalog/dedupe?dry_run=true");
      setDedupeState({ phase: "preview", ...data });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Duplikat-Analyse fehlgeschlagen");
      setDedupeState(null);
    }
  };

  const runDedupe = async () => {
    setDedupeState((s) => ({ ...s, phase: "running" }));
    try {
      const { data } = await api.post("/admin/pv-catalog/dedupe?dry_run=false");
      toast.success(`${data.actually_deactivated} Duplikate deaktiviert`);
      setDedupeState({ phase: "done", ...data });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Dedupe fehlgeschlagen");
      setDedupeState(null);
    }
  };

  const onImportFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    e.target.value = ""; // reset input für nächste Wahl
    setImportState({ phase: "extract", filename: f.name });
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/pv-catalog/import-extract", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,
      });
      const rows = (data.rows || []).map((r, i) => ({ ...r, _key: `imp-${i}` }));
      if (!rows.length) {
        toast.error("Keine Zeilen erkannt.");
        setImportState(null);
        return;
      }
      setImportState({
        phase: "preview",
        rows,
        source_tag: data.source_tag,
        detected_format: data.detected_format,
        filename: f.name,
      });
      toast.success(`${rows.length} Positionen erkannt — bitte prüfen`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import fehlgeschlagen");
      setImportState(null);
    }
  };

  const updateImportRow = (idx, patch) => {
    setImportState((st) => {
      if (!st || st.phase !== "preview") return st;
      const next = [...st.rows];
      next[idx] = { ...next[idx], ...patch };
      return { ...st, rows: next };
    });
  };

  const removeImportRow = (idx) => {
    setImportState((st) => {
      if (!st || st.phase !== "preview") return st;
      return { ...st, rows: st.rows.filter((_, i) => i !== idx) };
    });
  };

  const commitImport = async () => {
    if (!importState || importState.phase !== "preview") return;
    const clean = importState.rows.map(({ _key, ...r }) => ({
      type: r.type,
      brand: r.brand,
      model: r.model,
      kwp_or_kw: Number(r.kwp_or_kw) || 0,
      price_eur: Number(r.price_eur) || 0,
      unit: r.unit || (r.type === "addon" ? "pauschal" : "Stk"),
      specs: r.specs || {},
    }));
    setImportState({ ...importState, phase: "commit" });
    try {
      const { data } = await api.post("/admin/pv-catalog/import-commit", {
        rows: clean,
        source_tag: importState.source_tag,
      });
      toast.success(`${data.inserted} Positionen importiert${data.duplicates?.length ? ` · ${data.duplicates.length} Duplikate markiert` : ""}`);
      setImportState(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Commit fehlgeschlagen");
      setImportState({ ...importState, phase: "preview" });
    }
  };

  const activeType = TYPES.find((t) => t.key === activeTab) || TYPES[0];

  return (
    <div className="min-h-screen bg-[#09090B]" data-testid="admin-pv-catalog">
      {!embedded && <AppHeader title="PV-Komponenten" back="/dashboard" />}
      <main className="max-w-6xl mx-auto px-3 sm:px-4 py-4 sm:py-6 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
          <div className="flex items-center gap-3">
            <Sun className="text-[#00E575]" size={22} />
            <div>
              <h1 className="font-display text-xl sm:text-2xl font-bold">PV-Komponenten</h1>
              <p className="text-[12px] text-zinc-300 font-medium">Module, Wechselrichter, Speicher, Wallbox & Zusatzleistungen — Komponenten für den PV-Konfigurator · Preis-Spalte anklicken zum direkten Bearbeiten</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <label className="text-[12px] text-zinc-400 inline-flex items-center gap-2 cursor-pointer font-medium">
              <input
                type="checkbox" checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                data-testid="pv-catalog-toggle-inactive"
                className="accent-[#00E575]"
              />
              Inaktive anzeigen
            </label>
            <button
              onClick={seedDemo} disabled={seeding}
              data-testid="pv-catalog-seed"
              className="h-9 px-3 rounded-lg bg-[#18181B] border border-zinc-800 hover:border-[#00E575]/40 text-[12px] inline-flex items-center gap-1.5 disabled:opacity-50 font-medium"
              title="Beispiel-Komponenten laden (15 Stück)"
            >
              {seeding ? <Loader2 size={13} className="animate-spin" /> : <Package size={13} />}
              Beispiele
            </button>
            <button
              onClick={load} data-testid="pv-catalog-reload"
              className="h-9 px-3 rounded-lg bg-[#18181B] border border-zinc-800 hover:border-[#00E575]/40 text-[12px] inline-flex items-center gap-1.5 font-medium"
            >
              <RefreshCw size={13} /> Aktualisieren
            </button>
            <input
              ref={importFileRef}
              type="file"
              accept="application/pdf,image/*,.xlsx,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={onImportFile}
              className="hidden"
              data-testid="pv-catalog-import-input"
            />
            <button
              onClick={startImport}
              disabled={!!importState}
              data-testid="pv-catalog-import-btn"
              className="h-9 px-3 rounded-lg bg-[#18181B] border border-amber-500/40 hover:border-amber-500 text-amber-300 text-[12px] inline-flex items-center gap-1.5 disabled:opacity-50 font-medium"
              title="Excel/PDF/Bild einer Preisliste hochladen — Positionen werden extrahiert"
            >
              <Upload size={13} /> Datei-Import
            </button>
            <button
              onClick={openDedupePreview}
              disabled={!!dedupeState}
              data-testid="pv-catalog-dedupe-btn"
              className="h-9 px-3 rounded-lg bg-[#18181B] border border-rose-500/40 hover:border-rose-500 text-rose-300 text-[12px] inline-flex items-center gap-1.5 disabled:opacity-50 font-medium"
              title="Doppelte Komponenten (gleiche Marke + Modell + Typ) finden und deaktivieren"
            >
              <Filter size={13} /> Duplikate bereinigen
            </button>
            <button
              onClick={openNew} data-testid="pv-catalog-new"
              className="h-9 px-3 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[12px] inline-flex items-center gap-1.5 hover:bg-[#00C964]"
            >
              <Plus size={14} /> Neu
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 flex-wrap" role="tablist">
          {TYPES.map((t) => {
            const Icon = t.icon;
            const isActive = activeTab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                data-testid={`pv-catalog-tab-${t.key}`}
                className={`px-4 py-2 rounded-lg text-[12.5px] font-bold inline-flex items-center gap-2 transition-all border ${
                  isActive
                    ? "bg-[#00E575]/10 text-[#00E575] border-[#00E575]/40"
                    : "bg-[#18181B] text-zinc-400 border-zinc-800 hover:border-zinc-600"
                }`}
              >
                <Icon size={14} /> {t.label}
                <span className={`text-[12px] px-1.5 py-0.5 rounded ${isActive ? "bg-[#00E575]/20" : "bg-zinc-800"}`}>
                  {counts[t.key] || 0}
                </span>
              </button>
            );
          })}
        </div>

        {/* Loading */}
        {loading && (
          <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-8 flex items-center justify-center gap-2 text-zinc-200 font-semibold">
            <Loader2 size={16} className="animate-spin" /> lädt…
          </div>
        )}

        {/* List */}
        {!loading && tabRows.length === 0 && (
          <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-8 text-center text-zinc-300 text-sm font-medium">
            Noch keine {activeType.label} angelegt. „Beispiele“ lädt Demo-Komponenten.
          </div>
        )}
        {!loading && tabRows.length > 0 && (
          <section className="bg-[#18181B] border border-zinc-800 rounded-2xl p-4">
            <div className="overflow-x-auto -mx-1">
              <table className="w-full text-[12.5px]">
                <thead>
                  <tr className="text-[12px] uppercase tracking-widest text-zinc-300 font-semibold border-b border-zinc-800">
                    <SortableTh label="Marke" col="brand" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} align="left" />
                    <SortableTh label={activeTab === "addon" ? "Bezeichnung" : "Modell"} col="model" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} align="left" />
                    {activeTab !== "addon" && <SortableTh label={activeType.unitLabel} col="kwp_or_kw" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} align="right" hiddenMobile />}
                    <th className="text-right py-2 px-2">Einheit</th>
                    <SortableTh label="Preis (netto)" col="price_eur" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} align="right" />
                    <th className="text-center py-2 px-2">Status</th>
                    <th className="text-right py-2 px-2">Aktion</th>
                  </tr>
                </thead>
                <tbody>
                {tabRows.map((r) => {
                  const isEditing = inlineEdit && inlineEdit.id === r.id;
                  return (
                    <tr key={r.id} className={`border-b border-zinc-800/50 ${!r.active ? "opacity-50" : ""}`} data-testid={`pv-catalog-row-${r.id}`}>
                      <td className="py-2 px-2 text-zinc-100 font-semibold">{r.brand}</td>
                      <td className="py-2 px-2 text-zinc-300">{r.model}</td>
                      {activeTab !== "addon" && <td className="py-2 px-2 text-right text-zinc-400 hidden sm:table-cell">{r.kwp_or_kw || "—"}</td>}
                      <td className="py-2 px-2 text-right text-zinc-300 text-[12px]">{r.unit || "Stk"}</td>
                      <td className="py-2 px-2 text-right font-mono">
                        {isEditing ? (
                          <div className="inline-flex items-center gap-1 justify-end">
                            <input
                              autoFocus type="text" inputMode="decimal"
                              value={inlineEdit.value}
                              onChange={(e) => setInlineEdit({ ...inlineEdit, value: e.target.value })}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") { e.preventDefault(); commitInlineEdit(); }
                                if (e.key === "Escape") { e.preventDefault(); cancelInlineEdit(); }
                              }}
                              disabled={inlineEdit.saving}
                              data-testid={`pv-catalog-inline-price-input-${r.id}`}
                              className="h-8 w-28 bg-[#09090B] border border-[#00E575] rounded px-2 text-right text-[12px] text-zinc-50 outline-none disabled:opacity-50 font-medium"
                              placeholder="0,00" />
                            <button onClick={commitInlineEdit} disabled={inlineEdit.saving}
                              data-testid={`pv-catalog-inline-price-save-${r.id}`}
                              title="Speichern (Enter)"
                              className="h-7 w-7 rounded bg-[#00E575] text-[#09090B] hover:bg-[#00C964] inline-flex items-center justify-center disabled:opacity-50">
                              {inlineEdit.saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                            </button>
                            <button onClick={cancelInlineEdit} disabled={inlineEdit.saving}
                              data-testid={`pv-catalog-inline-price-cancel-${r.id}`}
                              title="Abbrechen (Esc)"
                              className="h-7 w-7 rounded bg-zinc-800 hover:bg-zinc-700 inline-flex items-center justify-center">
                              <X size={12} />
                            </button>
                          </div>
                        ) : (
                          <button onClick={() => startInlineEdit(r)}
                            data-testid={`pv-catalog-inline-price-${r.id}`}
                            title="Klicken zum Bearbeiten"
                            className="group text-right hover:bg-zinc-800/60 rounded px-2 py-0.5 -mx-2 inline-flex items-center gap-1 text-zinc-100">
                            {eur(r.price_eur)}
                            <Pencil size={9} className="text-zinc-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                          </button>
                        )}
                      </td>
                      <td className="py-2 px-2 text-center">
                        {r.active !== false ? (
                          <span className="text-[12px] font-bold uppercase text-[#00E575]">aktiv</span>
                        ) : (
                          <span className="text-[12px] font-bold uppercase text-amber-400">inaktiv</span>
                        )}
                      </td>
                      <td className="py-2 px-2 text-right">
                        <div className="inline-flex gap-1">
                          <button
                            onClick={() => openEdit(r)}
                            data-testid={`pv-catalog-edit-${r.id}`}
                            className="h-8 w-8 rounded-lg bg-[#09090B] border border-zinc-700 hover:border-[#00E575]/50 inline-flex items-center justify-center"
                            title="Bearbeiten"
                          ><Pencil size={13} /></button>
                          <button
                            onClick={() => duplicateRow(r)}
                            data-testid={`pv-catalog-duplicate-${r.id}`}
                            className="h-8 w-8 rounded-lg bg-sky-500/10 border border-sky-500/40 text-sky-300 hover:bg-sky-500/20 inline-flex items-center justify-center"
                            title="Kopieren (Duplikat als neue Komponente)"
                          ><Copy size={13} /></button>
                          {r.active !== false ? (
                            <button
                              onClick={() => remove(r)}
                              data-testid={`pv-catalog-del-${r.id}`}
                              className="h-8 w-8 rounded-lg bg-[#09090B] border border-zinc-700 hover:border-red-500/50 hover:text-red-400 inline-flex items-center justify-center"
                              title="Deaktivieren"
                            ><Trash2 size={13} /></button>
                          ) : (
                            <button
                              onClick={() => reactivate(r)}
                              data-testid={`pv-catalog-act-${r.id}`}
                              className="h-8 px-2 rounded-lg bg-[#09090B] border border-emerald-700 text-emerald-300 hover:bg-emerald-500/10 text-[12px] font-bold uppercase"
                            >aktivieren</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </main>

      {/* Import-Modal: LLM-Extraktion + editierbare Preview + Commit */}
      {importState && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-2 sm:p-4 overflow-y-auto"
             data-testid="pv-import-modal">
          <div className="bg-[#18181B] border border-zinc-700 rounded-2xl max-w-6xl w-full max-h-[92vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between border-b border-zinc-800 px-5 py-4">
              <div className="flex items-center gap-3 min-w-0">
                <Upload size={20} className="text-amber-400 shrink-0" />
                <div className="min-w-0">
                  <h2 className="font-display text-lg font-bold">Komponenten-Import via {importState.detected_format === "xlsx" ? "Excel" : importState.detected_format === "image" ? "Bild" : "PDF"}</h2>
                  <p className="text-[12px] text-zinc-400 mt-0.5 truncate">
                    {importState.filename || "—"}
                    {" · "}
                    {importState.phase === "extract" && "LLM analysiert …"}
                    {importState.phase === "preview" && `${importState.rows.length} Positionen erkannt — bitte prüfen und ggf. korrigieren`}
                    {importState.phase === "commit" && "Speichern …"}
                  </p>
                </div>
              </div>
              {importState.phase !== "extract" && importState.phase !== "commit" && (
                <button
                  onClick={() => setImportState(null)}
                  data-testid="pv-import-cancel"
                  className="h-8 w-8 rounded-lg bg-zinc-800 hover:bg-zinc-700 inline-flex items-center justify-center"
                >
                  <X size={14} />
                </button>
              )}
            </div>

            {importState.phase === "extract" && (
              <div className="p-10 flex flex-col items-center gap-3 text-zinc-400">
                <Loader2 size={28} className="animate-spin text-[#00E575]" />
                <p className="text-[13px]">Positionen werden aus <strong className="text-zinc-100">{importState.filename}</strong> extrahiert …</p>
                <p className="text-[12px] text-zinc-300">Kann bei großen PDFs bis zu 60 Sekunden dauern.</p>
              </div>
            )}

            {importState.phase === "preview" && (
              <>
                <div className="flex-1 overflow-y-auto px-3 sm:px-5 py-3">
                  <div className="bg-amber-500/5 border border-amber-500/30 rounded-lg p-3 mb-3 flex items-start gap-2">
                    <AlertTriangle size={14} className="text-amber-400 mt-0.5 shrink-0" />
                    <p className="text-[11.5px] text-zinc-300 leading-snug">
                      Preise, Typen und Bezeichnungen können vor dem Commit direkt in der Tabelle geändert werden. Zeilen entfernen: <span className="text-red-400">✕</span>-Icon.
                      Duplikate (Marke + Modell) werden beim Commit markiert, aber nicht blockiert — Sie können anschließend manuell entscheiden.
                    </p>
                  </div>
                  <table className="w-full text-[12px] font-medium">
                    <thead className="sticky top-0 bg-[#18181B] z-10">
                      <tr className="text-zinc-400 border-b border-zinc-800">
                        <th className="text-left py-2 pr-2 font-bold uppercase tracking-widest text-[12px]">Typ</th>
                        <th className="text-left py-2 pr-2 font-bold uppercase tracking-widest text-[12px]">Marke</th>
                        <th className="text-left py-2 pr-2 font-bold uppercase tracking-widest text-[12px]">Modell/Bezeichnung</th>
                        <th className="text-right py-2 pr-2 font-bold uppercase tracking-widest text-[12px]">kWp/kW/kWh</th>
                        <th className="text-right py-2 pr-2 font-bold uppercase tracking-widest text-[12px]">Preis netto</th>
                        <th className="text-left py-2 pr-2 font-bold uppercase tracking-widest text-[12px]">Einheit</th>
                        <th className="text-center py-2 pr-2 font-bold uppercase tracking-widest text-[12px]">Info</th>
                        <th className="text-right py-2 font-bold uppercase tracking-widest text-[12px]"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {importState.rows.map((r, i) => (
                        <ImportRowFragment
                          key={r._key}
                          idx={i}
                          row={r}
                          onUpdate={updateImportRow}
                          onRemove={removeImportRow}
                          types={TYPES}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex items-center justify-between gap-3 border-t border-zinc-800 px-5 py-3 bg-[#0F0F11]">
                  <div className="text-[11.5px] text-zinc-400">
                    <strong className="text-zinc-100">{importState.rows.length}</strong> Positionen · Summe netto: <strong className="text-[#00E575]">
                      {eur(importState.rows.reduce((s, r) => s + (Number(r.price_eur) || 0), 0))}
                    </strong>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setImportState(null)}
                      data-testid="pv-import-abort"
                      className="h-9 px-3 rounded-lg bg-[#18181B] border border-zinc-700 text-[12px] font-medium"
                    >
                      Abbrechen
                    </button>
                    <button
                      onClick={commitImport}
                      disabled={!importState.rows.length}
                      data-testid="pv-import-commit"
                      className="h-9 px-4 rounded-lg bg-[#00E575] hover:bg-[#00C964] text-[#09090B] font-bold text-[12px] inline-flex items-center gap-1.5 disabled:opacity-50"
                    >
                      <CheckCircle2 size={14} /> {importState.rows.length} Positionen importieren
                    </button>
                  </div>
                </div>
              </>
            )}

            {importState.phase === "commit" && (
              <div className="p-10 flex flex-col items-center gap-3 text-zinc-400">
                <Loader2 size={28} className="animate-spin text-[#00E575]" />
                <p className="text-[13px]">Komponenten werden aktualisiert …</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Edit/Create Modal */}
      {editor && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-end sm:items-center justify-center p-2 sm:p-4 overflow-y-auto"
          onClick={() => !saving && setEditor(null)} data-testid="pv-catalog-modal">
          <div onClick={(e) => e.stopPropagation()} className="bg-[#18181B] border border-zinc-700 rounded-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between sticky top-0 bg-[#18181B] border-b border-zinc-800 px-5 py-4">
              <div>
                <h2 className="font-display text-lg font-bold">{editor.isNew ? "Neue Komponente" : "Komponente bearbeiten"}</h2>
                <p className="text-[12px] text-zinc-400 mt-0.5">{TYPES.find(t => t.key === editor.type)?.label}</p>
              </div>
              <button onClick={() => setEditor(null)} disabled={saving} className="h-8 w-8 rounded-lg bg-zinc-800 hover:bg-zinc-700 inline-flex items-center justify-center"><X size={14} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Typ *</label>
                  <select value={editor.type}
                    onChange={(e) => setEditor({ ...editor, type: e.target.value, unit: e.target.value === "addon" ? "pauschal" : "Stk" })}
                    data-testid="pv-catalog-edit-type" className={inputCls} disabled={!editor.isNew}>
                    {TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Einheit</label>
                  <select value={editor.unit || "Stk"}
                    onChange={(e) => setEditor({ ...editor, unit: e.target.value })}
                    data-testid="pv-catalog-edit-unit" className={inputCls}>
                    <option value="Stk">Stk</option>
                    <option value="pauschal">pauschal</option>
                    <option value="m">m (lfdm)</option>
                    <option value="m²">m²</option>
                    <option value="h">h (Stunde)</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Marke *</label>
                  <input type="text" value={editor.brand} onChange={(e) => setEditor({ ...editor, brand: e.target.value })}
                    data-testid="pv-catalog-edit-brand" placeholder={editor.type === "addon" ? "z.B. Eigen­leistung" : "z.B. Meyer Burger"} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>{editor.type === "addon" ? "Bezeichnung *" : "Modell *"}</label>
                  <input type="text" value={editor.model} onChange={(e) => setEditor({ ...editor, model: e.target.value })}
                    data-testid="pv-catalog-edit-model" placeholder={editor.type === "addon" ? "z.B. Gerüst inkl. Auf-/Abbau" : "z.B. Black 410W"} className={inputCls} />
                </div>
                {editor.type !== "addon" && (
                  <div>
                    <label className={labelCls}>{TYPES.find(t => t.key === editor.type)?.unitLabel}</label>
                    <input type="number" step="0.01" value={editor.kwp_or_kw}
                      onChange={(e) => setEditor({ ...editor, kwp_or_kw: e.target.value })}
                      data-testid="pv-catalog-edit-power" className={inputCls} />
                  </div>
                )}
                <div className={editor.type === "addon" ? "sm:col-span-2" : ""}>
                  <label className={labelCls}>Preis netto *</label>
                  <EuroInput value={editor.price_eur}
                    onChange={(v) => setEditor({ ...editor, price_eur: v })}
                    testid="pv-catalog-edit-price" className={inputCls} />
                </div>
              </div>
              <section>
                <label className="inline-flex items-center gap-2 text-[13px] text-zinc-200">
                  <input type="checkbox" checked={editor.active}
                    onChange={(e) => setEditor({ ...editor, active: e.target.checked })}
                    data-testid="pv-catalog-edit-active" className="accent-[#00E575]" />
                  Komponente ist aktiv (im Konfigurator sichtbar)
                </label>
              </section>

              {/* Iter88zz9-b — Marketing-Beschreibung + Eigenschaften + Vorteile
                  landen im Angebots-PDF unter dem Modellnamen. Der Text ersetzt
                  den Auto-Text der iter88zz9-Migration und wird 1:1 gerendert. */}
              <section className="space-y-3 border-t border-zinc-800 pt-4">
                <div className="flex items-center gap-2">
                  <h4 className="text-[12px] uppercase tracking-wider text-[#00E575] font-mono font-bold">Marketing-Texte für das Angebots-PDF</h4>
                  <span className="text-[10.5px] text-zinc-500">(erscheinen unter dem Modell in jeder Positionszeile)</span>
                </div>
                <div>
                  <label className={labelCls}>Beschreibung <span className="text-zinc-500 text-[10.5px]">— Fließtext, 1-3 Sätze</span></label>
                  <textarea
                    rows={3}
                    value={editor.specs?.description || ""}
                    onChange={(e) => setEditor({ ...editor, specs: { ...(editor.specs || {}), description: e.target.value } })}
                    data-testid="pv-catalog-edit-description"
                    placeholder="z.B. Monokristallines PERC-Modul, 400 Wp, 21,6 % Wirkungsgrad, schwarzer Rahmen. Hoher Wirkungsgrad für Wohn-Dachanlagen."
                    className={`${inputCls} min-h-[70px] whitespace-pre-wrap`}
                  />
                </div>
                <div>
                  <label className={labelCls}>Eigenschaften <span className="text-zinc-500 text-[10.5px]">— optional, Aufzählung mit „·" oder Zeilenumbrüchen</span></label>
                  <textarea
                    rows={2}
                    value={editor.specs?.properties || ""}
                    onChange={(e) => setEditor({ ...editor, specs: { ...(editor.specs || {}), properties: e.target.value } })}
                    data-testid="pv-catalog-edit-properties"
                    placeholder="z.B. 21,6 % Wirkungsgrad · 25 Jahre Leistungsgarantie · IEC 61215 / 61730 · schwarze Optik"
                    className={`${inputCls} min-h-[52px] whitespace-pre-wrap`}
                  />
                </div>
                <div>
                  <label className={labelCls}>Kundenvorteile <span className="text-zinc-500 text-[10.5px]">— optional, was der Kunde davon hat</span></label>
                  <textarea
                    rows={2}
                    value={editor.specs?.advantages || ""}
                    onChange={(e) => setEditor({ ...editor, specs: { ...(editor.specs || {}), advantages: e.target.value } })}
                    data-testid="pv-catalog-edit-advantages"
                    placeholder="z.B. Höherer Ertrag auf gleicher Fläche · unauffällige Optik · lange Garantie senkt Risiko"
                    className={`${inputCls} min-h-[52px] whitespace-pre-wrap`}
                  />
                </div>
                {/* Iter88zz9y — Produktbild (analog WP-Katalog) */}
                <div>
                  <label className={labelCls}>Produktbild — Datei oder URL</label>
                  <div className="flex gap-2">
                    <input
                      value={editor.image_url || ""}
                      onChange={(e) => setEditor({ ...editor, image_url: e.target.value })}
                      data-testid="pv-catalog-edit-image-url"
                      className={`${inputCls} flex-1`}
                      placeholder="https://…/produktbild.jpg oder Bild hochladen →"
                    />
                    <label
                      className={`inline-flex items-center gap-1.5 h-10 px-3 rounded-lg font-bold text-[12px] cursor-pointer transition ${
                        uploadingImg
                          ? "bg-zinc-700 text-zinc-400 cursor-wait"
                          : editor.isNew
                            ? "bg-zinc-800 text-zinc-500 cursor-not-allowed"
                            : "bg-[#00E575] text-[#09090B] hover:bg-[#00C964]"
                      }`}
                      title={editor.isNew ? "Erst Komponente speichern, dann Bild hochladen" : "Bild von Rechner hochladen (wird auf 800×800 zugeschnitten)"}
                      data-testid="pv-catalog-edit-image-upload-label"
                    >
                      {uploadingImg ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                      Upload
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        data-testid="pv-catalog-edit-image-file"
                        disabled={editor.isNew || uploadingImg}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) uploadComponentImage(f);
                          e.target.value = "";
                        }}
                      />
                    </label>
                  </div>
                  <p className="text-[12px] text-zinc-400 mt-1">
                    Direkt-Upload wird auf 800×800 px zugeschnitten (JPEG). Alternativ öffentliche HTTPS-URL eintragen. Bilder erscheinen im PDF-Layout 2 als kleine Icons neben der Position.
                  </p>
                  {editor.image_url ? (
                    <div className="mt-2 rounded-lg overflow-hidden bg-white inline-block border border-zinc-700">
                      <img
                        src={editor.image_url}
                        alt="Vorschau Produktbild"
                        data-testid="pv-catalog-edit-image-preview"
                        className="h-24 w-24 object-contain"
                        onError={(e) => { e.currentTarget.style.opacity = "0.3"; }}
                      />
                    </div>
                  ) : null}
                </div>
                {!editor.isNew && (
                  <PvDatasheetField
                    editor={editor}
                    onChange={(fields) => { setEditor((e) => ({ ...e, ...fields })); load(true); }}
                  />
                )}
              </section>
            </div>
            <div className="sticky bottom-0 bg-[#18181B] border-t border-zinc-800 px-5 py-3 flex justify-end gap-2">
              <button onClick={() => setEditor(null)} disabled={saving}
                className="h-9 px-4 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-[12px] text-zinc-200 font-medium">Abbrechen</button>
              <button onClick={save} disabled={saving} data-testid="pv-catalog-save"
                className="h-9 px-4 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[12px] inline-flex items-center gap-1.5 hover:bg-[#00C964] disabled:opacity-50">
                {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                {editor.isNew ? "Anlegen" : "Speichern"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dedupe Preview Modal */}
      {dedupeState && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="pv-dedupe-modal">
          <div className="bg-[#18181B] border border-rose-500/30 rounded-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
            <div className="sticky top-0 bg-[#18181B] border-b border-zinc-800 px-5 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Filter size={16} className="text-rose-300" />
                <h3 className="font-display text-lg font-semibold text-rose-100">Duplikate bereinigen</h3>
              </div>
              <button
                onClick={() => setDedupeState(null)}
                disabled={dedupeState.phase === "running"}
                className="text-zinc-400 hover:text-zinc-100 disabled:opacity-40"
                data-testid="pv-dedupe-close"
              >
                <X size={18} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4">
              {dedupeState.phase === "loading" && (
                <div className="flex items-center gap-2 text-sm text-zinc-400 py-8 justify-center">
                  <Loader2 className="animate-spin" size={14} /> Analysiere Katalog…
                </div>
              )}
              {(dedupeState.phase === "preview" || dedupeState.phase === "running") && dedupeState.groups && (
                <>
                  {dedupeState.groups.length === 0 ? (
                    <div className="flex flex-col items-center gap-2 py-8 text-center">
                      <CheckCircle2 size={40} className="text-emerald-400" />
                      <div className="text-emerald-300 font-semibold">Alles sauber</div>
                      <div className="text-xs text-zinc-300">Keine doppelten Komponenten gefunden.</div>
                    </div>
                  ) : (
                    <>
                      <div className="mb-3 text-sm text-zinc-300">
                        <span className="font-semibold text-rose-200">{dedupeState.would_deactivate}</span> Duplikate in{" "}
                        <span className="font-semibold text-rose-200">{dedupeState.duplicate_groups}</span> Gruppen gefunden.
                        Jüngere Kopien werden deaktiviert, der älteste Eintrag bleibt aktiv.
                      </div>
                      <div className="space-y-2" data-testid="pv-dedupe-groups">
                        {dedupeState.groups.map((g, i) => (
                          <div key={i} className="bg-[#0F0F11] border border-zinc-800 rounded-lg p-3">
                            <div className="flex items-center justify-between gap-2 mb-1.5">
                              <div className="text-[13px] font-semibold text-zinc-100">
                                {g.brand} · {g.model}
                              </div>
                              <div className="text-[12px] uppercase tracking-wider text-zinc-300">{g.type} · {g.count}×</div>
                            </div>
                            <div className="text-[12px] text-emerald-300 mb-1">
                              ✓ KEEP · {g.keep.id.slice(0,8)} · seit {(g.keep.created_at || "").slice(0,10)}
                            </div>
                            {g.delete.map((d) => (
                              <div key={d.id} className="text-[12px] text-rose-300">
                                ✗ DEL · {d.id.slice(0,8)} · seit {(d.created_at || "").slice(0,10)}
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </>
              )}
              {dedupeState.phase === "done" && (
                <div className="flex flex-col items-center gap-2 py-8 text-center">
                  <CheckCircle2 size={40} className="text-emerald-400" />
                  <div className="text-emerald-300 font-semibold">Fertig</div>
                  <div className="text-sm text-zinc-300">
                    <span className="font-semibold">{dedupeState.actually_deactivated}</span> Duplikat(e) deaktiviert.
                  </div>
                  <div className="text-[12px] text-zinc-300">Bleiben in der DB als inaktiv erhalten (Audit-Trail bleibt).</div>
                </div>
              )}
            </div>

            <div className="sticky bottom-0 bg-[#18181B] border-t border-zinc-800 px-5 py-3 flex justify-end gap-2">
              {dedupeState.phase === "done" ? (
                <button
                  onClick={() => setDedupeState(null)}
                  data-testid="pv-dedupe-done-btn"
                  className="h-9 px-4 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[12px] hover:bg-[#00C964]"
                >
                  Schließen
                </button>
              ) : (
                <>
                  <button
                    onClick={() => setDedupeState(null)}
                    disabled={dedupeState.phase === "running"}
                    className="h-9 px-4 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-[12px] text-zinc-200 disabled:opacity-40 font-medium"
                  >
                    Abbrechen
                  </button>
                  {dedupeState.phase === "preview" && dedupeState.groups?.length > 0 && (
                    <button
                      onClick={runDedupe}
                      data-testid="pv-dedupe-confirm-btn"
                      className="h-9 px-4 rounded-lg bg-rose-500 text-white font-bold text-[12px] inline-flex items-center gap-1.5 hover:bg-rose-600"
                    >
                      <Trash2 size={13} /> {dedupeState.would_deactivate} Duplikat(e) deaktivieren
                    </button>
                  )}
                  {dedupeState.phase === "running" && (
                    <button disabled className="h-9 px-4 rounded-lg bg-rose-500/50 text-white font-bold text-[12px] inline-flex items-center gap-1.5">
                      <Loader2 size={13} className="animate-spin" /> Deaktiviere…
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// Iter88n (2026-02-19) — Wiederverwendbarer Sortier-Header.
// Klick auf den Header togglet asc/desc; der aktive Sortier-Zustand wird
// durch ein ▲/▼ visualisiert. Die Spalte selbst ist visuell hervorgehoben.
function SortableTh({ label, col, sortKey, sortDir, onClick, align = "left", hiddenMobile = false }) {
  const active = sortKey === col;
  const alignClass = align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left";
  const hideClass = hiddenMobile ? "hidden sm:table-cell" : "";
  return (
    <th
      onClick={() => onClick(col)}
      className={`${alignClass} ${hideClass} py-2 px-2 cursor-pointer select-none hover:text-emerald-300 transition ${active ? "text-emerald-300" : ""}`}
      data-testid={`sort-th-${col}`}
      title={`Nach ${label} sortieren${active ? " (umschalten)" : ""}`}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <span className={`text-[10px] ${active ? "opacity-100" : "opacity-30"}`}>
          {active ? (sortDir === "desc" ? "▼" : "▲") : "↕"}
        </span>
      </span>
    </th>
  );
}
